import { useState, useEffect, memo, useCallback } from "react";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonBadge from "components/ArgonBadge";
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Business as VendorIcon,
  MoreVert as MoreIcon
} from "@mui/icons-material";

function ChatRoomList({ 
  rooms, 
  selectedRoom, 
  onRoomSelect, 
  onCreateRoom, 
  onJoinRoom,
  currentUser,
  loading 
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomType, setNewRoomType] = useState("group");
  const [newRoomDescription, setNewRoomDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [filteredRooms, setFilteredRooms] = useState([]);

  useEffect(() => {
    if (rooms) {
      const filtered = rooms.filter(room =>
        room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        room.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRooms(filtered);
    }
  }, [rooms, searchTerm]);

  const handleCreateRoom = useCallback(async () => {
    if (!newRoomName.trim()) return;
    
    try {
      await onCreateRoom({
        name: newRoomName,
        type: newRoomType,
        description: newRoomDescription,
        is_private: isPrivate
      });
      
      setShowCreateDialog(false);
      setNewRoomName("");
      setNewRoomDescription("");
      setIsPrivate(false);
    } catch (error) {
      console.error("Failed to create room:", error);
    }
  }, [newRoomName, newRoomType, newRoomDescription, isPrivate, onCreateRoom]);

  const getRoomIcon = (type) => {
    switch (type) {
      case "group":
        return <GroupIcon />;
      case "direct":
        return <PersonIcon />;
      case "support":
        return <SupportIcon />;
      case "vendor":
        return <VendorIcon />;
      default:
        return <GroupIcon />;
    }
  };

  const getRoomTypeColor = (type) => {
    switch (type) {
      case "group":
        return "info";
      case "direct":
        return "success";
      case "support":
        return "warning";
      case "vendor":
        return "secondary";
      default:
        return "info";
    }
  };

  const formatLastMessage = (message) => {
    if (!message) return "No messages yet";
    
    const maxLength = 40;
    let text = message.content;
    
    if (message.type === "file") {
      text = `📎 ${message.file_name || "File"}`;
    } else if (message.type === "image") {
      text = "🖼️ Image";
    }
    
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return "";
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    
    return date.toLocaleDateString();
  };

  return (
    <ArgonBox
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 2,
        overflow: "hidden"
      }}
    >
      {/* Header */}
      <ArgonBox
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "grey.300",
          backgroundColor: "grey.50"
        }}
      >
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <ArgonTypography variant="h6" fontWeight="bold">
            Chat Rooms
          </ArgonTypography>
          <ArgonButton
            variant="gradient"
            color="info"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
          >
            New
          </ArgonButton>
        </ArgonBox>
        
        <ArgonInput
          placeholder="Search rooms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={<SearchIcon sx={{ color: "grey.500", mr: 1 }} />}
          fullWidth
          size="small"
        />
      </ArgonBox>

      {/* Room List */}
      <ArgonBox sx={{ flex: 1, overflow: "auto" }}>
        {loading ? (
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography variant="body2" color="text">
              Loading rooms...
            </ArgonTypography>
          </ArgonBox>
        ) : filteredRooms.length === 0 ? (
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography variant="body2" color="text">
              {searchTerm ? "No rooms found" : "No chat rooms yet"}
            </ArgonTypography>
          </ArgonBox>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredRooms.map((room, index) => (
              <div key={room.id}>
                <ListItem
                  button
                  selected={selectedRoom?.id === room.id}
                  onClick={() => onRoomSelect(room)}
                  sx={{
                    py: 2,
                    px: 2,
                    "&.Mui-selected": {
                      backgroundColor: "primary.50",
                      borderRight: "3px solid",
                      borderRightColor: "primary.main"
                    },
                    "&:hover": {
                      backgroundColor: "grey.50"
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        backgroundColor: `${getRoomTypeColor(room.type)}.main`,
                        color: "white"
                      }}
                    >
                      {getRoomIcon(room.type)}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{
                            maxWidth: "60%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {room.name}
                        </ArgonTypography>
                        <ArgonBox display="flex" alignItems="center" gap={0.5}>
                          {room.unread_count > 0 && (
                            <ArgonBadge
                              variant="gradient"
                              color="error"
                              size="xs"
                              badgeContent={room.unread_count > 99 ? "99+" : room.unread_count}
                            />
                          )}
                          <ArgonTypography variant="caption" color="text">
                            {formatLastMessageTime(room.last_message?.created_at)}
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>
                    }
                    secondary={
                      <ArgonBox mt={0.5}>
                        <ArgonTypography
                          variant="body2"
                          color="text"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: room.unread_count > 0 ? "bold" : "normal"
                          }}
                        >
                          {formatLastMessage(room.last_message)}
                        </ArgonTypography>
                        <ArgonBox display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Chip
                            label={room.type}
                            size="small"
                            color={getRoomTypeColor(room.type)}
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                          <ArgonTypography variant="caption" color="text">
                            {room.participant_count} members
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>
                    }
                  />
                  
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <MoreIcon fontSize="small" />
                  </IconButton>
                </ListItem>
                {index < filteredRooms.length - 1 && <Divider />}
              </div>
            ))}
          </List>
        )}
      </ArgonBox>

      {/* Create Room Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Chat Room</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Room Name"
            fullWidth
            variant="outlined"
            value={newRoomName}
            onChange={(e) => setNewRoomName(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Room Type</InputLabel>
            <Select
              value={newRoomType}
              onChange={(e) => setNewRoomType(e.target.value)}
              label="Room Type"
            >
              <MenuItem value="group">Group Chat</MenuItem>
              <MenuItem value="support">Support Channel</MenuItem>
              <MenuItem value="vendor">Vendor Communication</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newRoomDescription}
            onChange={(e) => setNewRoomDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
              />
            }
            label="Private Room (Invite Only)"
          />
        </DialogContent>
        <DialogActions>
          <ArgonButton onClick={() => setShowCreateDialog(false)}>
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="gradient"
            color="info"
            onClick={handleCreateRoom}
            disabled={!newRoomName.trim()}
          >
            Create Room
          </ArgonButton>
        </DialogActions>
      </Dialog>
    </ArgonBox>
  );
}

ChatRoomList.propTypes = {
  rooms: PropTypes.array,
  selectedRoom: PropTypes.object,
  onRoomSelect: PropTypes.func.isRequired,
  onCreateRoom: PropTypes.func.isRequired,
  onJoinRoom: PropTypes.func,
  currentUser: PropTypes.object,
  loading: PropTypes.bool
};

ChatRoomList.defaultProps = {
  rooms: [],
  selectedRoom: null,
  onJoinRoom: () => {},
  currentUser: null,
  loading: false
};

export default memo(ChatRoomList);